import { API_URL } from '../../config';

type AuthHeadersFn = (json?: boolean) => HeadersInit;

export interface OpLog {
  id: string;
  group_id: number;
  started_by: number;
  started_by_email: string;
  title: string | null;
  started_at: string;
  stopped_at: string | null;
  status: string;
  entry_count?: number;
  type_breakdown?: Record<string, number>;
}

export interface OpLogEntry {
  id: number;
  ts: string;
  event_type: string;
  source: string;
  summary: string;
  data: Record<string, unknown>;
}

async function fetchJson<T>(url: string, headers: HeadersInit): Promise<T> {
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

export async function getActiveLog(h: AuthHeadersFn): Promise<OpLog | null> {
  return fetchJson(`${API_URL}/api/oplog/active`, h());
}

export async function startLog(h: AuthHeadersFn, title?: string): Promise<OpLog> {
  const response = await fetch(`${API_URL}/api/oplog/start`, {
    method: 'POST',
    headers: h(true),
    body: JSON.stringify(title ? { title } : {}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || 'Failed to start log');
  }
  return response.json();
}

export async function stopLog(h: AuthHeadersFn): Promise<OpLog> {
  const response = await fetch(`${API_URL}/api/oplog/stop`, {
    method: 'POST',
    headers: h(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || 'Failed to stop log');
  }
  return response.json();
}

export async function getLogs(h: AuthHeadersFn, offset = 0, limit = 20): Promise<{ logs: OpLog[]; total: number }> {
  return fetchJson(`${API_URL}/api/oplog?offset=${offset}&limit=${limit}`, h());
}

export async function getLog(h: AuthHeadersFn, logId: string): Promise<OpLog> {
  return fetchJson(`${API_URL}/api/oplog/${logId}`, h());
}

export async function getLogEntries(
  h: AuthHeadersFn,
  logId: string,
  params?: { event_type?: string; search?: string; offset?: number; limit?: number }
): Promise<{ entries: OpLogEntry[]; total: number }> {
  const sp = new URLSearchParams();
  if (params?.event_type) sp.set('event_type', params.event_type);
  if (params?.search) sp.set('search', params.search);
  sp.set('offset', String(params?.offset || 0));
  sp.set('limit', String(params?.limit || 50));
  return fetchJson(`${API_URL}/api/oplog/${logId}/entries?${sp.toString()}`, h());
}

export async function deleteLog(h: AuthHeadersFn, logId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/oplog/${logId}`, {
    method: 'DELETE',
    headers: h(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || 'Failed to delete log');
  }
}

export async function downloadExport(h: AuthHeadersFn, logId: string, format: 'csv' | 'pdf'): Promise<void> {
  const response = await fetch(`${API_URL}/api/oplog/${logId}/export/${format}`, { headers: h() });
  if (!response.ok) throw new Error(`Export failed: ${response.status}`);
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || `operation_log.${format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
